import {
    Box,
    Button,
    FormLabel,
    Grid,
    MenuItem,
    Select,
    SelectChangeEvent,
    styled,
    TextField,
} from "@mui/material";
import { ChangeEvent, useState } from "react";
import { IWithClassName } from "../../../../theme/default.theme";
import { ToleranceMaxRange } from "../../const/tolerance";
import { FormParams } from "../../enums/form-params";
import { Steps } from "../../enums/steps";
import { TolerancePeriod, ToleranceRange } from "../../enums/tolerance";

interface IProps extends IWithClassName {
    tolerance: number;
    toleranceRange: ToleranceRange;
    tolerancePeriod: TolerancePeriod;
    onSelectChange: (
        step: Steps,
        label: FormParams,
    ) => (options: string | number) => void;
}

const Tolerance = ({
    tolerance,
    tolerancePeriod,
    toleranceRange,
    onSelectChange,
    className,
}: IProps & IWithClassName) => {
    const [maxRnge, setMaxRange] = useState<number>(
        ToleranceMaxRange[tolerancePeriod],
    );
    const [period, setPeriod] = useState<TolerancePeriod>(tolerancePeriod);
    const [range, setRange] = useState<ToleranceRange>(toleranceRange);
    const [duration, setDuration] = useState<number>(tolerance);
    const [editTolerance, setEditTolerance] = useState<boolean>(false);
    const handlePeriodChange = (event: SelectChangeEvent<TolerancePeriod>) => {
        const value = event.target.value;
        switch (value) {
            case TolerancePeriod.DAYS:
                setPeriod(TolerancePeriod.DAYS);
                setMaxRange(ToleranceMaxRange[TolerancePeriod.DAYS]);
                break;
            case TolerancePeriod.YEARS:
                setPeriod(TolerancePeriod.YEARS);
                setMaxRange(ToleranceMaxRange[TolerancePeriod.YEARS]);
                break;
            case TolerancePeriod.MONTHS:
                setPeriod(TolerancePeriod.MONTHS);
                setMaxRange(ToleranceMaxRange[TolerancePeriod.MONTHS]);
        }
        setDuration(1);
    };

    const handleRangeChange = (event: SelectChangeEvent<ToleranceRange>) => {
        const rangeKey = Object.entries(ToleranceRange).find(
            ([, value]) => value === event.target.value,
        )?.[0] as keyof typeof ToleranceRange;
        setRange(ToleranceRange[rangeKey]);
    };

    const handleDurationChange = (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => setDuration(parseInt(event.target.value, 10));

    const applyChanges = () => {
        onSelectChange(
            Steps.LIST_DOWNLOAD_OPTIONS,
            FormParams.TOLERANCE,
        )(duration);
        onSelectChange(
            Steps.LIST_DOWNLOAD_OPTIONS,
            FormParams.TOLERANCE_RANGE,
        )(range);
        onSelectChange(
            Steps.LIST_DOWNLOAD_OPTIONS,
            FormParams.TOLERANCE_PERIOD,
        )(period);
    };

    const resetForm = () => {
        setDuration(tolerance);
        setPeriod(tolerancePeriod);
        setRange(toleranceRange);
        setEditTolerance(false);
    };
    return (
        <Grid item className={className}>
            <Box>
                <FormLabel className="tolerance-label">
                    Include closest <span>{tolerance} </span>
                    <span>{tolerancePeriod} </span>
                    <span>
                        {toleranceRange === ToleranceRange.BOTH
                            ? "Before/After"
                            : toleranceRange}{" "}
                    </span>{" "}
                    snapshot date:
                </FormLabel>
                {!editTolerance ? (
                    <Button
                        className="edit-button"
                        variant="outlined"
                        color="secondary"
                        onClick={() => setEditTolerance(true)}
                    >
                        Edit Tolerance
                    </Button>
                ) : (
                    <Box className="content-inline multi-row">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={applyChanges}
                        >
                            Apply
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={resetForm}
                        >
                            Cancel
                        </Button>
                        <TextField
                            type="number"
                            variant="standard"
                            className="number-box"
                            value={duration}
                            id="duration"
                            onChange={handleDurationChange}
                            InputProps={{
                                inputProps: { min: 1, max: maxRnge },
                            }}
                        />
                        <Select
                            variant="standard"
                            label="Period"
                            className="flex-grow"
                            value={period}
                            onChange={handlePeriodChange}
                        >
                            <MenuItem value={TolerancePeriod.DAYS}>
                                Days
                            </MenuItem>
                            <MenuItem value={TolerancePeriod.MONTHS}>
                                Months
                            </MenuItem>
                            <MenuItem value={TolerancePeriod.YEARS}>
                                Years
                            </MenuItem>
                        </Select>
                        <Select
                            variant="standard"
                            label="Range"
                            value={range}
                            onChange={handleRangeChange}
                        >
                            <MenuItem value={ToleranceRange.AFTER}>
                                After
                            </MenuItem>
                            <MenuItem value={ToleranceRange.BEFORE}>
                                Before
                            </MenuItem>
                            <MenuItem value={ToleranceRange.BOTH}>
                                Before/After
                            </MenuItem>
                        </Select>
                    </Box>
                )}
            </Box>
        </Grid>
    );
};

export default styled(Tolerance)(() => ({
    ".content-inline": {
        display: "flex",
        flexDirection: "row",
        gap: 5,
    },
    ".label-center": {
        margin: "auto 0",
    },
    ".MuiButton-root": {
        height: 30,
    },
    ".number-box": {
        width: 50,
    },
    ".multi-row": {
        justifyContent: "space-between",
        gap: 5,
        width: "100%",
    },
    ".flex-grow": {
        flexGrow: 1,
    },
    ".action-cont": {
        float: "right",
    },
    ".tolerance-label  span": {
        textTransform: "capitalize",
    },
    ".edit-button": {
        display: "flex",
    },
}));
